import { useEffect } from "react";
import {
  requestListUserSubscription,
  useAuthState,
  useSubscriptionDispatch,
  useSubscriptionState,
} from "../Context";
import {
  isFreeBusinessCreateBlocked,
  isFreeMember,
} from "../Constants/business";

/**
 * Loads the signed-in user's live PackageSubscription rows once per session and
 * answers "is this a free member?" from them.
 *
 * `User.membership` is denormalized and lags behind checkout and expiry, which
 * is why the backend lifecycle sweep (`is_owner_free`) ignores it and reads
 * live subscriptions instead. Any gate that has to agree with the server has to
 * do the same, or a user who just paid stays blocked (and one whose plan lapsed
 * stays unblocked) until the flag catches up.
 *
 * Until the list has loaded, `isFreeMember` falls back to `user.membership`, so
 * the gate degrades to the old behaviour rather than flickering open.
 *
 * The query is `subscriptionsByUserExpiredAt` with `expiredAt > now`, so only
 * live rows come back; the first page is enough to answer "any paid plan?".
 */
export const useLiveSubscriptions = () => {
  const { user } = useAuthState();
  const dispatch = useSubscriptionDispatch();
  const { userSubscriptions, userSubscriptionsLoaded, loading } =
    useSubscriptionState();

  useEffect(() => {
    if (user?.id && !userSubscriptionsLoaded && !loading) {
      requestListUserSubscription(dispatch, { userID: user.id, page: 1 });
    }
    // Deliberately keyed on identity only: a failed request leaves `loading`
    // false and `loaded` false, and re-running on those would retry forever.
    // The gate falls back to `user.membership` until the next mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.id]);

  return userSubscriptionsLoaded ? userSubscriptions : undefined;
};

// `isFreeMember`, resolved against live subscriptions when they are available.
export const useIsFreeMember = () => {
  const { user } = useAuthState();
  return isFreeMember(user, useLiveSubscriptions());
};

// The free-tier "1 business" create gate, resolved the same way.
export const useFreeBusinessCreateBlocked = (businesses) => {
  const { user } = useAuthState();
  return isFreeBusinessCreateBlocked(user, businesses, useLiveSubscriptions());
};
